import { LIMIT } from '@/constants';
import { fetcher } from '@/services/graphql/fetcher/index';
import { CustomComponentType, CustomPageType, Sort } from '@/types';
import {
  fetchBoundDistributorDocument,
  fetchCheckinRewardsConfigDocument,
  fetchConsecutiveDaysDocument,
  fetchCustomerCheckinDocument,
  fetchCustomerCheckinRecordsDocument,
  fetchCustomerPrizeRecordsDocument,
  fetchCustomPageDocument,
  fetchDesDeCodeDocument,
  fetchDistributorSharingDocument,
  fetchGetAnnouncementDocument,
  fetchGetDistributorDocument,
  fetchOrderTrackingDocument,
  fetchSeriesProductsDocument,
  fetchVerifyExternalOrderRewardDocument,
  fetchChannelAppointmentPointsCostDocument,
  fetchCreateShopAppointmentDocument,
} from '../graphql/docs';

/**
 * 获取自定义页面数据
 * @param type - 自定义页面类型
 */
export const fetchCustomData = async (
  type: CustomPageType | undefined,
  customPageId: string = '',
  isPreview: boolean = false
) => {
  const res = await fetcher.query(fetchCustomPageDocument, {
    variables: {
      customPageId,
      isPreview,
      options: {
        filter: {
          type: { eq: type },
          enable: { eq: true },
        },
      },
    },
  });
  const data = res.data.value?.customPage;
  if (data) {
    // 取商品组件类型的第一个，即只渲染一个商品分类组件
    const index = data.components.findIndex(
      (it: AnyObject) => it.type === CustomComponentType.product
    );
    if (index >= 0) {
      data.components[index]._render = true;
    }
    return data;
  }
};

/**
 * 获取活动页面数据
 * @param type - 自定义页面类型
 */
export const fetchActivityCustomData = async (customPageId: string, isPreview: boolean = false) => {
  const res = await fetcher.query(fetchCustomPageDocument, {
    variables: {
      customPageId,
      isPreview,
      // options: {
      //   filter: {
      //     enable: { eq: true },
      //   },
      // },
    },
  });
  const data = res.data.value?.customPage;
  if (data) {
    // 取商品组件类型的第一个，即只渲染一个商品分类组件
    const index = data.components.findIndex(
      (it: AnyObject) => it.type === CustomComponentType.product
    );
    if (index >= 0) {
      data.components[index]._render = true;
    }
    return data;
  }
};

/**
 * 根据分类id获取商品数据
 * @param collectionId - 分类id
 * @returns
 */
export const fetchSeriesProductsByCollectionId = async (collectionId: string) => {
  const res = await fetcher.query(fetchSeriesProductsDocument, {
    variables: {
      collectionId,
      options: {
        filter: {
          hidden: { eq: false },
          freeGift: { eq: false },
        },
        sort: { id: Sort.ASC, createdAt: Sort.DESC },
      },
    },
  });
  return res.data.value?.seriesProducts?.items;
};

/**
 * 根据分类id获取商品数据
 * @param collectionId - 分类id
 * @returns
 */
export const fetchSeriesProductsByCollection = async (variables: AnyObject) => {
  const res = await fetcher.query(fetchSeriesProductsDocument, {
    variables: {
      collectionId: variables.collectionId,
      options: {
        skip: LIMIT * variables.options.skip,
        take: variables.options?.take || LIMIT,
        filter: {
          hidden: { eq: false },
          freeGift: { eq: false },
          name: { contains: variables.options?.name },
        },
        sort: { id: Sort.ASC, createdAt: Sort.DESC },
      },
    },
  });
  return res.data.value?.seriesProducts;
};

/**
 * 查询自己的分销员信息
 */
export const fetchGetDistributor = async () => {
  const res = await fetcher.query(fetchGetDistributorDocument, {});
  return res.data.value?.getDistributor;
};

/**
 * 根据类型获取分享信息
 * @param input - {type, path}
 * @returns
 */
export const fetchDistributorSharing = async (input: AnyObject) => {
  const res = await fetcher.query(fetchDistributorSharingDocument, {
    variables: {
      input,
    },
  });
  return res.data.value?.distributorSharing;
};

/**
 * 绑定分销员
 * @param distributorId - 分销id
 * @returns
 */
export const fetchBoundDistributor = async (distributorId: string) => {
  const res = await fetcher.mutation(fetchBoundDistributorDocument, {
    distributorId,
  });
  return res.data?.boundDistributor;
};

/**
 * 获取通告信息
 */
export const fetchGetAnnouncement = async () => {
  const res = await fetcher.query(fetchGetAnnouncementDocument, {
    variables: {},
  });
  return res.data.value?.getAnnouncement;
};

/**
 * 引导页面用户下单上报数据
 */
export const fetchOrderTracking = async (
  orderId: string,
  trackingPageType: string,
  trackingPageId: string
) => {
  const res = await fetcher.mutation(fetchOrderTrackingDocument, {
    orderId,
    trackingPageType,
    trackingPageId,
  });

  return res.data.value?.addOrderTracking;
};

// 解析code
export const fetchDesCode = async (input: string) => {
  const res = await fetcher.query(fetchDesDeCodeDocument, {
    variables: {
      input,
    },
  });
  return res.data.value?.desDecode;
};

/**
 * 获取签到配置
 * @returns
 */
export const fetchCheckinRewardsConfig = async () => {
  const res = await fetcher.query(fetchCheckinRewardsConfigDocument, {
    variables: {},
  });
  return res.data.value?.checkinRewardsConfig;
};

/**
 * 获取签到记录
 * @returns
 */
export const fetchCustomerCheckinRecords = async (input: AnyObject) => {
  const res = await fetcher.query(fetchCustomerCheckinRecordsDocument, {
    variables: { input },
  });
  return res.data.value?.customerCheckinRecords;
};

/**
 * 获取连签天数
 * @returns
 */
export const fetchConsecutiveDays = async () => {
  const res = await fetcher.query(fetchConsecutiveDaysDocument, {
    variables: {},
  });
  return res.data.value?.consecutiveDays;
};

/**
 * 获取连签奖励
 * @returns
 */
export const fetchCustomerPrizeRecords = async () => {
  const res = await fetcher.query(fetchCustomerPrizeRecordsDocument, {
    variables: {},
  });
  return res.data.value?.customerPrizeRecords;
};

/**
 * 签到
 * @returns
 */
export const fetchCustomerCheckin = async () => {
  const res = await fetcher.mutation(fetchCustomerCheckinDocument, {});
  return res.data?.customerCheckin;
};

/**
 * 购物金转换
 * @returns
 */
export const fetchVerifyExternalOrderReward = async (code: string) => {
  const res = await fetcher.mutation(fetchVerifyExternalOrderRewardDocument, {
    code
  });
  return res.data?.verifyExternalOrderReward;
};

/**
 * 获取门店预约消耗的积分
 * @returns
 */
export const fetchChannelAppointmentPointsCost = async () => {
  const res = await fetcher.query(fetchChannelAppointmentPointsCostDocument, {});
  return res.data.value?.channelAppointmentPointsCost;
};

/**
 * 提交门店预约
 * @returns
 */
export const fetchCreateShopAppointment = async (input: AnyObject) => {
  const res = await fetcher.mutation(fetchCreateShopAppointmentDocument, {
    input
  });
  return res.data?.createShopAppointment;
};
